import { createInstanceNode } from "../schema/instanceNode.schema";
import { resolveWallPlacement } from "../room/resolveWallPlacement";

const DEFAULTS_BY_RUN_TYPE = {
  base: {
    height: 720,
    depth: 560,
    floorOffset: 0,
  },

  wall: {
    height: 700,
    depth: 320,
    floorOffset: 1400,
  },

  tall: {
    height: 2000,
    depth: 560,
    floorOffset: 0,
  },
};

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function createWallsFromIntent(intent = {}) {
  if (Array.isArray(intent?.room?.walls) && intent.room.walls.length) {
    return intent.room.walls;
  }

  if (Array.isArray(intent?.walls) && intent.walls.length) {
    return intent.walls;
  }

  const wallA = toNumber(intent?.room?.wallA, toNumber(intent?.room?.width, 3000));
  const wallB = toNumber(intent?.room?.wallB, toNumber(intent?.room?.depth, 0));
  const height = toNumber(intent?.room?.height, 2800);
  const thickness = toNumber(intent?.room?.wallThickness, 100);

  const walls = [
    {
      id: "back_wall",
      name: "Back wall",
      start: { x: 0, z: 0 },
      end: { x: wallA, z: 0 },
      height,
      thickness,
      inward: "positive_z",
    },
  ];

  if (wallB > 0 || intent.layoutType === "l_shape" || intent.layoutType === "u_shape") {
    walls.push({
      id: "left_wall",
      name: "Left wall",
      start: { x: 0, z: 0 },
      end: { x: 0, z: wallB || 2200 },
      height,
      thickness,
      inward: "positive_x",
    });
  }

  if (intent.layoutType === "u_shape") {
    walls.push({
      id: "right_wall",
      name: "Right wall",
      start: { x: wallA, z: 0 },
      end: { x: wallA, z: wallB || 2200 },
      height,
      thickness,
      inward: "negative_x",
    });
  }

  return walls;
}

function findWallById(walls = [], wallId) {
  return walls.find((wall) => String(wall.id) === String(wallId));
}

function createLineId() {
  return `line_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function createLine(offset) {
  return {
    id: createLineId(),
    offset,
  };
}

function makeSplitLines(count, total) {
  const n = toNumber(count, 0);
  const size = toNumber(total, 0);

  if (n <= 0 || size <= 0) return [];

  if (n === 1) {
    return [createLine(size)];
  }

  return Array.from({ length: n - 1 }, (_, index) =>
    createLine(Math.round((size / n) * (index + 1)))
  );
}

function findDefinitionByCategory(definitions = [], category) {
  return definitions.find(
    (definition) =>
      String(definition.category || "").toLowerCase() ===
      String(category || "").toLowerCase()
  );
}

function findCabinetDefinition(definitions = [], type = "base") {
  const normalizedType = String(type || "base").toLowerCase();

  if (normalizedType === "wall") {
    return (
      findDefinitionByCategory(definitions, "cabinet.wall") ||
      findDefinitionByCategory(definitions, "cabinet.box") ||
      definitions.find((definition) =>
        String(definition.category || "").toLowerCase().includes("cabinet.wall")
      ) ||
      definitions.find((definition) =>
        String(definition.category || "").toLowerCase().includes("cabinet")
      )
    );
  }

  if (normalizedType === "tall") {
    return (
      findDefinitionByCategory(definitions, "cabinet.tall") ||
      findDefinitionByCategory(definitions, "cabinet.box") ||
      definitions.find((definition) =>
        String(definition.category || "").toLowerCase().includes("cabinet.tall")
      ) ||
      definitions.find((definition) =>
        String(definition.category || "").toLowerCase().includes("cabinet")
      )
    );
  }

  return (
    findDefinitionByCategory(definitions, "cabinet.base") ||
    findDefinitionByCategory(definitions, "cabinet.box") ||
    definitions.find((definition) =>
      String(definition.category || "").toLowerCase().includes("cabinet.base")
    ) ||
    definitions.find((definition) =>
      String(definition.category || "").toLowerCase().includes("cabinet")
    )
  );
}

function makeMaterialProjectParameters(materials = {}) {
  const result = {};

  [
    "corpusBoardMaterialId",
    "corpusEdgeMaterialId",
    "frontBoardMaterialId",
    "frontEdgeMaterialId",
    "backBoardMaterialId",
    "backEdgeMaterialId",
    "shelfBoardMaterialId",
    "shelfEdgeMaterialId",
  ].forEach((key) => {
    if (
      materials[key] !== undefined &&
      materials[key] !== null &&
      materials[key] !== ""
    ) {
      result[key] = Number(materials[key]);
    }
  });

  return result;
}

function getRunDirection(rotationY) {
  const rad = toNumber(rotationY, 0) * (Math.PI / 180);

  return {
    x: Math.cos(rad),
    z: -Math.sin(rad),
  };
}

function normalizeCabinetRole(cabinet = {}) {
  const role = String(cabinet.cabinetRole || "").toLowerCase();
  const appliance = String(cabinet.applianceType || "").toLowerCase();
  const label = String(
    cabinet.label || cabinet.code || cabinet.note || ""
  ).toLowerCase();

  if (role) return role;

  if (label.includes("sink") || appliance === "sink") return "sink_base";
  if (label.includes("dishwasher") || appliance === "dishwasher")
    return "dishwasher";
  if (label.includes("drawer")) return "drawer_base";
  if (label.includes("door")) return "door_base";
  if (label.includes("fridge") || appliance === "fridge") return "fridge_tower";
  if (label.includes("oven") || appliance === "oven") return "oven_tower";
  if (label.includes("pantry")) return "standard";
  if (label.includes("corner")) return "corner";

  return "standard";
}

function normalizeApplianceType(cabinet = {}) {
  const appliance = String(cabinet.applianceType || "").toLowerCase();
  const role = normalizeCabinetRole(cabinet);
  const label = String(
    cabinet.label || cabinet.code || cabinet.note || ""
  ).toLowerCase();

  if (appliance && appliance !== "none") return appliance;

  if (role === "sink_base" || label.includes("sink")) return "sink";
  if (role === "dishwasher" || label.includes("dishwasher"))
    return "dishwasher";
  if (role === "fridge_tower" || label.includes("fridge")) return "fridge";
  if (role === "oven_tower" || label.includes("oven")) return "oven";

  return "none";
}

function getCabinetRunType(cabinet = {}, run = {}) {
  const role = normalizeCabinetRole(cabinet);
  const appliance = normalizeApplianceType(cabinet);

  if (
    role === "fridge_tower" ||
    role === "oven_tower" ||
    appliance === "fridge" ||
    appliance === "oven"
  ) {
    return "tall";
  }

  return String(cabinet.type || run.type || "base").toLowerCase();
}

function normalizeFrontType(cabinet = {}) {
  const role = normalizeCabinetRole(cabinet);
  const appliance = normalizeApplianceType(cabinet);

  if (role === "dishwasher" || appliance === "dishwasher") return "none";
  if (role === "appliance_space") return "open";
  if (role === "open_shelf") return "open";
  if (role === "drawer_base") return "drawers";
  if (role === "door_base") return "doors";
  if (role === "sink_base") return "doors";
  if (role === "fridge_tower") return "doors";
  if (role === "oven_tower") return "doors";

  return cabinet.frontType || cabinet.front || "open";
}

function getCabinetDisplayName(cabinet, definition) {
  if (cabinet.label) return cabinet.label;
  if (cabinet.code) return cabinet.code;

  const role = normalizeCabinetRole(cabinet);
  const appliance = normalizeApplianceType(cabinet);

  if (role === "sink_base" || appliance === "sink") return "Sink base";
  if (role === "dishwasher" || appliance === "dishwasher") return "Dishwasher";
  if (role === "oven_tower" || appliance === "oven") return "Oven cabinet";
  if (role === "fridge_tower" || appliance === "fridge") return "Fridge cabinet";
  if (role === "corner") return "Corner cabinet";
  if (role === "drawer_base") return "Drawer base";
  if (role === "door_base") return "Door base";
  if (role === "open_shelf") return "Open shelf";

  return definition.name || definition.code || "AI Cabinet";
}

function buildFrontPartitions(cabinet, width, height) {
  const frontType = String(normalizeFrontType(cabinet)).toLowerCase();

  if (frontType === "none" || frontType === "open") {
    return undefined;
  }

  if (frontType === "doors") {
    const doorCount = toNumber(cabinet.doorCount, width >= 700 ? 2 : 1);

    return {
      front: {
        horizontal: [],
        vertical: makeSplitLines(doorCount, width),
        panelOverrides: {},
      },
    };
  }

  if (frontType === "drawers") {
    const drawerCount = toNumber(cabinet.drawerCount, 3);

    return {
      front: {
        horizontal: makeSplitLines(drawerCount, height),
        vertical: [],
        panelOverrides: {},
      },
    };
  }

  return undefined;
}

function createCabinetNode({
  definition,
  run,
  cabinet,
  localOffset,
  walls = [],
}) {
  const runType = getCabinetRunType(cabinet, run);
  const defaults = DEFAULTS_BY_RUN_TYPE[runType] || DEFAULTS_BY_RUN_TYPE.base;

  const width = toNumber(cabinet.width, 600);

  const height = toNumber(
    cabinet.height,
    toNumber(run.defaultHeight, defaults.height)
  );

  const depth = toNumber(
    cabinet.depth,
    toNumber(run.defaultDepth, defaults.depth)
  );

  const floorOffset = toNumber(
    cabinet.floorOffset,
    toNumber(run.floorOffset, defaults.floorOffset)
  );

  let rotationY = toNumber(run.rotationY, 0);
let centerX;
let centerZ;

const wall = run.wallId ? findWallById(walls, run.wallId) : null;
let wallPlacement = null;

if (wall) {
  wallPlacement = resolveWallPlacement({
    wall,
    offsetFromWallStart:
      toNumber(run.offsetFromWallStart, 0) + toNumber(localOffset, 0),
    width,
    height,
    depth,
    elevation: floorOffset,
    snap: 10,
    clampToWall: true,
  });

  centerX = wallPlacement.transform.position.x;
  centerZ = wallPlacement.transform.position.z;
  rotationY = wallPlacement.transform.rotation.y;
} else {
  const direction = getRunDirection(rotationY);

  const startX = toNumber(run.startX, 0);
  const startZ = toNumber(run.startZ, 0);

  const centerAlongRun = localOffset + width / 2;

  centerX = startX + direction.x * centerAlongRun;
  centerZ = startZ + direction.z * centerAlongRun;
}

  const node = createInstanceNode();

  node.definitionId = definition.id;
  node.name = getCabinetDisplayName(cabinet, definition);

  node.transform = {
    position: {
      x: Number(centerX.toFixed(3)),
      y: height / 2 + floorOffset,
      z: Number(centerZ.toFixed(3)),
    },
    rotation: {
      x: 0,
      y: rotationY,
      z: 0,
    },
  };

  if (wall && wallPlacement?.placement) {
  node.placement = {
    ...wallPlacement.placement,
    wallId: run.wallId,
    runId: run.id,
    offsetFromWallStart:
      toNumber(run.offsetFromWallStart, 0) + toNumber(localOffset, 0),
    alignBackToWall: true,
  };
}

  node.parameterBindings = {};

  node.parameterOverrides = {
    width,
    height,
    depth,
    floorOffset,

    shelfCount: toNumber(cabinet.shelfCount, 0),

    frontGapOuter: toNumber(cabinet.frontGapOuter, 2),
    frontGapInner: toNumber(cabinet.frontGapInner, 2),

    cabinetRole: normalizeCabinetRole(cabinet),
    applianceType: normalizeApplianceType(cabinet),
  };

  const partitions = buildFrontPartitions(cabinet, width, height);

  if (partitions) {
    node.partitions = partitions;
  }

  node.children = [];

  return {
    node,
    width,
    depth,
  };
}

function normalizeRuns(intent = {}) {
  if (Array.isArray(intent.runs) && intent.runs.length) {
    return intent.runs;
  }

  const fallbackCabinets = intent.cabinets || intent.cabinetLine || [];

  return [
    {
      id: "main_base",
      type: "base",
      startX: 0,
      startZ: 0,
      rotationY: 0,
      defaultHeight: 720,
      defaultDepth: 560,
      floorOffset: 0,
      cabinets: fallbackCabinets,
    },
  ];
}

export function generateSceneFromKitchenIntent({
  intent,
  definitions = [],
}) {
  const runs = normalizeRuns(intent);
  const walls = createWallsFromIntent(intent);
  const nodes = [];

  runs.forEach((run) => {
    let cursor = 0;

    (run.cabinets || []).forEach((cabinet) => {
      const cabinetRunType = getCabinetRunType(cabinet, run);
      const definition = findCabinetDefinition(definitions, cabinetRunType);

      if (!definition) {
        console.warn("No cabinet definition found for AI cabinet:", cabinet);
        return;
      }

      const width = toNumber(cabinet.width, 600);
const localOffset = cursor;

      const created = createCabinetNode({
  definition,
  run: {
    ...run,
    type: cabinetRunType,
  },
  cabinet,
  localOffset,
  walls,
});

      nodes.push(created.node);

      cursor += created.width;
    });
  });

  return {
    projectParametersPatch: makeMaterialProjectParameters(intent.materials || {}),
    nodes,
  };
}